import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNumbers } from "@/context/NumberContext";
import { fetchNumberMembers, ROLE_LABELS } from "@/lib/api";
import { initials } from "@/lib/format";

const roleColor = {
  admin: "text-emerald-700 bg-emerald-50",
  superviseur: "text-blue-700 bg-blue-50",
  agent: "text-slate-600 bg-slate-100",
};

export const TeamMembersList = ({ className = "" }) => {
  const { selectedId, selectedNumber } = useNumbers();
  const perLine = !!selectedId && selectedId !== "all";
  const { data: members = [] } = useQuery({
    queryKey: ["number-members", selectedId],
    queryFn: () => fetchNumberMembers(selectedId),
    enabled: perLine,
  });

  if (!perLine) return null;

  return (
    <div className={className} data-testid="team-members-list">
      <div className="flex items-center justify-between px-1 mb-2">
        <p className="text-xs tracking-[0.06em] uppercase font-semibold text-slate-400">Équipe</p>
        <span className="text-xs text-slate-400">{members.length}</span>
      </div>
      <p className="px-1 text-xs text-slate-400 mb-3 leading-snug">
        Membres assignés à {selectedNumber?.line_name || "ce numéro"}
      </p>
      <div className="space-y-1">
        {members.map((m) => (
          <div
            key={m.id}
            data-testid={`team-member-${m.id}`}
            className="flex items-center gap-2.5 px-1 py-1.5 rounded-lg"
          >
            {m.avatar ? (
              <img src={m.avatar} alt={m.name} className="h-7 w-7 rounded-full object-cover border border-slate-200" />
            ) : (
              <span className="h-7 w-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-500">
                {initials(m.name)}
              </span>
            )}
            <div className="min-w-0 flex-1 leading-tight">
              <p className="text-sm font-medium text-slate-800 truncate">{m.name}</p>
              <p className="text-[11px] text-slate-400 truncate">{m.email}</p>
            </div>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${roleColor[m.role] || roleColor.agent}`}>
              {ROLE_LABELS[m.role] || m.role}
            </span>
          </div>
        ))}
        {members.length === 0 && (
          <p className="px-1 text-xs text-slate-400 italic">Aucun membre assigné.</p>
        )}
      </div>
    </div>
  );
};
